import type { CSSProperties, ImgHTMLAttributes } from "react";
import { cwfPublicUrl } from "@/lib/cwfPublicUrl";

export type CwfImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "width" | "height"
> & {
  src: string | { src: string; height?: number; width?: number };
  alt: string;
  width?: number;
  height?: number;
  /** Cover parent; parent should be `position: relative` and sized. */
  fill?: boolean;
  quality?: number;
  priority?: boolean;
  placeholder?: "empty" | "blur";
  blurDataURL?: string;
  sizes?: string;
  onLoadingComplete?: (img: HTMLImageElement) => void;
};

function resolveSrc(
  src: string | { src: string; height?: number; width?: number },
): string {
  if (typeof src === "string") return src;
  return src.src;
}

/** Drop-in subset of `next/image` for Vite static / FRVR folder hosting. */
export function CwfImage({
  src,
  alt,
  width,
  height,
  fill,
  className,
  style,
  priority,
  onLoadingComplete,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  sizes: _sizes,
  quality: _quality,
  ...rest
}: CwfImageProps) {
  const href = cwfPublicUrl(resolveSrc(src));
  const imgClass = fill
    ? ["absolute inset-0 h-full w-full", className].filter(Boolean).join(" ")
    : className;
  const imgStyle: CSSProperties | undefined =
    fill === true
      ? { objectFit: "cover", ...style }
      : style;

  return (
    <img
      src={href}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      className={imgClass}
      style={imgStyle}
      decoding={priority ? "sync" : "async"}
      loading={priority ? "eager" : "lazy"}
      onLoad={(e) => onLoadingComplete?.(e.currentTarget)}
      {...rest}
    />
  );
}

export default CwfImage;
